/**
 * Types for the staff side of the Service Desk (the queue, the ticket workspace,
 * and the settings pages). The user-facing shapes (SdTicketSummary, SdComment,
 * SdFormField and friends) come from `@ampersandbrands/ui`; these extend them
 * with what only desk staff receive.
 *
 * Built to the api contract in `docs/servicedesk-api.md` (api repo). Where the
 * contract says "full ticket" or "the four policy rows" without listing fields,
 * the shapes below are the Cockpit side's best reading and are marked GUESS so
 * the api slice can be checked against them quickly.
 */

import type {
  SdAudience,
  SdCategoryNode,
  SdComment,
  SdFieldOption,
  SdFieldType,
  SdFormField,
  SdKind,
  SdPriority,
  SdStatus,
  SdTicketDetail,
  SdTicketSummary,
} from '@ampersandbrands/ui';

/** Desk approver lists keyed by purpose (today only `hr_exit`, the Employee exit form). */
/** One store on the Sweet Spot pilot list. */
export type SdPilotStore = { store_no: string; name: string | null; added_by: string | null; added_at: string | null };

export type SdApproverLists = Record<string, { email: string; name: string | null }[]>;

export type SdSlaState = 'ok' | 'warning' | 'breached' | 'paused' | 'done';

/** One queue row: the summary plus the staff-only columns the contract lists. */
export type SdStaffTicketRow = SdTicketSummary & {
  /** The per-desk ticket number (the numeric part of the display key). */
  number?: number;
  reporter_name?: string | null;
  reporter_email?: string | null;
  store_no?: string | null;
  assignee_email?: string | null;
  sla_state: SdSlaState;
  first_response_due_at?: string | null;
  resolve_due_at?: string | null;
  kind?: SdKind | null;
  source_app?: string | null;
};

export type SdQueuePage = { rows: SdStaffTicketRow[]; next_cursor: string | null };

export type SdQueueFilters = {
  status: SdStatus[];
  priority: SdPriority[];
  /** `me`, `unassigned`, an email, or '' for anyone. */
  assignee: string;
  category_id: number | null;
  store_no: string;
  sla: '' | 'breached' | 'warning';
  q: string;
  sort: 'last_activity' | 'created' | 'due';
};

export type SdTicketEvent = {
  id: number;
  event_type: string;
  actor_name?: string | null;
  actor_email?: string | null;
  created_at: string;
  data?: Record<string, unknown> | null;
};

export type SdStaffComment = SdComment & {
  /** GUESS: ids of people @mentioned in this comment, for display only. */
  mentions?: { email: string; name?: string | null }[];
};

/** GUESS: the staff ticket = the user detail plus the fields the contract lists for staff. */
export type SdStaffTicketDetail = Omit<SdTicketDetail, 'comments'> & {
  comments: SdStaffComment[];
  events: SdTicketEvent[];
  desk_slug?: string;
  category_id: number | null;
  kind?: SdKind | null;
  source_app?: string | null;
  source_route?: string | null;
  reporter_email?: string | null;
  assignee_email?: string | null;
  hr_restricted: boolean;
  vendor?: string | null;
  sla_state: SdStaffTicketRow['sla_state'];
  first_response_due_at?: string | null;
  resolve_due_at?: string | null;
  confirmed_bug?: boolean;
  watchers?: { email: string; name?: string | null }[];
  vendor_emails?: {
    id: number;
    direction: 'outbound' | 'inbound';
    subject?: string | null;
    created_at: string;
  }[];
};

export type SdTicketPatch = Partial<{
  status: SdStatus;
  priority: SdPriority;
  assignee_email: string | null;
  category_id: number;
  title: string;
  vendor_ticket_no: string | null;
  hr_restricted: boolean;
}>;

export type SdStaffCommentInput = {
  visibility: 'public' | 'internal';
  body_html: string;
  body_text: string;
  attachments: { filename: string; content_type: string; base64: string }[];
};

export type SdStats = {
  open: number;
  unassigned: number;
  breached: number;
  due_today: number;
  /** Counts by status (contract: "counts by status"). */
  by_status?: Partial<Record<SdStatus, number>>;
};

export type SdDeskSummary = { slug: string; name: string };

// --- Configuration -------------------------------------------------------

/** One node of the full category tree (inactive included) for the settings editor. */
export type SdCategoryAdminNode = Omit<SdCategoryNode, 'children'> & {
  parent_id: number | null;
  /** GUESS: list of audiences ('corp', 'stores', 'it', ...). */
  audience: SdAudience[];
  vendor_route?: string | null;
  team_hint?: string | null;
  page_key?: string | null;
  hr_restricted: boolean;
  is_active: boolean;
  sort: number;
  open_ticket_count?: number;
  children: SdCategoryAdminNode[];
};

export type SdCategoryInput = {
  parent_id?: number | null;
  name: string;
  description?: string | null;
  audience: SdAudience[];
  kind?: SdKind | null;
  default_priority?: SdPriority | null;
  vendor_route?: string | null;
  team_hint?: string | null;
  page_key?: string | null;
  hr_restricted?: boolean;
  is_active?: boolean;
  sort?: number;
};

export type SdFieldAdmin = Omit<SdFormField, 'options'> & {
  category_id: number | null;
  options?: SdFieldOption[] | null;
  audience?: SdAudience[] | null;
  sort: number;
  is_active?: boolean;
};

export type SdFieldInput = {
  category_id: number | null;
  field_key: string;
  label: string;
  help_text?: string | null;
  field_type: SdFieldType;
  options?: SdFieldOption[] | null;
  person_scope?: 'store' | 'corp' | 'all' | null;
  allow_corporate?: boolean | null;
  required: boolean;
  show_if?: { field_key: string; equals: string[] } | null;
  audience?: SdAudience[] | null;
  sort?: number;
};

export type SdSlaRow = {
  priority: SdPriority;
  first_response_minutes: number;
  resolve_minutes: number;
  business_hours_only: boolean;
  /** Fraction of the time used before the chip turns to warning (0.75 = at 75%). Column name per the migration. */
  warn_at_fraction: number;
};

/** One open range per entry, `[start, end]` as HH:MM. An empty list means closed that day (matches the migration seed). */
export type SdDayHours = [string, string][];
export const SD_WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type SdWeekday = (typeof SD_WEEKDAYS)[number];

/** Business hours JSON is keyed by three-letter weekday (shape taken from the migration seed). */
export type SdDeskConfig = {
  slug: string;
  name: string;
  timezone: string;
  business_hours: Partial<Record<SdWeekday, SdDayHours>>;
  sla_247_priorities: SdPriority[];
  vendor_mailbox?: string | null;
  sender?: string | null;
  /** The vendors a category can send tickets to. Absent until the api ships the list. */
  vendors?: SdVendor[];
};

export type SdVendor = { id: string; name: string; support_email?: string | null };

export type SdTopic = { id: number; slug: string; name: string; is_active: boolean };

export type SdAutomation = {
  id: number;
  rule_key: string;
  name: string;
  description?: string | null;
  /** 'event' or 'schedule'. */
  trigger_type?: string | null;
  enabled: boolean;
  config: Record<string, unknown>;
  last_run_at?: string | null;
  last_run_summary?: string | null;
};

export type SdAutomationRun = {
  id: number;
  ticket_id?: number | null;
  started_at: string;
  finished_at?: string | null;
  /** ok, skipped, or error. */
  outcome?: string | null;
  detail?: string | null;
};

// --- Knowledge base -------------------------------------------------------

export type SdArticleType = 'howto' | 'troubleshooting' | 'policy' | 'runbook';
export type SdArticleStatus = 'draft' | 'published' | 'retired';

export type SdArticleCategory = { id: number; path: string };

export type SdStaffArticleRow = {
  id: number;
  slug: string;
  title: string;
  summary?: string | null;
  article_type: SdArticleType;
  status: SdArticleStatus;
  audience: SdAudience[];
  /** Optional labels (articles only). */
  topics: string[];
  /** Topic slugs, for round-tripping an edit. */
  topic_slugs?: string[];
  /** Related nodes of the category tree, as full paths. */
  categories: SdArticleCategory[];
  updated_at: string;
};

export type SdArticleVersion = {
  version: number;
  created_at: string;
  author_name?: string | null;
  change_note?: string | null;
};

export type SdStaffArticle = SdStaffArticleRow & {
  body_html: string;
  /** Plain text of the saved body, kept in step with it for search. */
  body_text?: string;
  version: number;
  versions: SdArticleVersion[];
};

export type SdArticleInput = {
  title: string;
  slug?: string;
  summary: string;
  article_type: SdArticleType;
  audience: SdAudience[];
  topics: string[];
  category_ids: number[];
  body_html: string;
  body_text: string;
  change_note?: string;
};

export type { SdStatus, SdPriority, SdAudience };

/** `GET /v1/servicedesk/people/contact?email=`: how to reach a person, and where they work when it is a store. */
export type SdPersonContact = {
  name: string;
  email: string;
  title?: string | null;
  department?: string | null;
  phone?: string | null;
  mobile?: string | null;
  extension?: string | null;
  teams_chat_url?: string | null;
  store?: {
    store_no?: string | null;
    name?: string | null;
    phone?: string | null;
    timezone?: string | null;
    local_time?: string | null;
    local_time_label?: string | null;
    open_now?: boolean | null;
    hours_today?: string | null;
    manager_name?: string | null;
    manager_email?: string | null;
  } | null;
};
